import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, MapPin } from "lucide-react";
import { api, USER_ID, track, money } from "../api";
import { rewardToast } from "../components";
import { ModuleGate } from "../flags";

export default function JourneyPage() {
  return (
    <ModuleGate flag="journeys">
      <JourneyInner />
    </ModuleGate>
  );
}

function JourneyInner() {
  const { slug } = useParams();
  const [journey, setJourney] = useState(null);

  const load = useCallback(() => api.get(`/journeys/${slug}`, { params: { user_id: USER_ID } }).then((r) => setJourney(r.data)), [slug]);
  useEffect(() => {
    load();
    track("journey_started", { slug });
  }, [slug, load]);

  if (!journey) return <div className="p-6 text-white/50 text-sm">Loading journey…</div>;

  const done = journey.completed_steps || [];
  const pct = Math.round((done.length / journey.steps.length) * 100);

  const completeStep = async (i) => {
    const r = await api.post(`/journeys/${slug}/step`, { user_id: USER_ID, step_index: i });
    rewardToast(r.data);
    if (r.data.journey_completed) {
      const { toast } = await import("sonner");
      toast.success(`JOURNEY COMPLETE — ${journey.name}`, { style: { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" } });
    }
    load();
  };

  return (
    <div data-testid={`journey-page-${slug}`} className="px-4 pt-6 pb-10 mx-auto w-full max-w-3xl">
      <p className="text-[10px] tracking-[0.35em] text-[#D4AF37]">FOOD JOURNEY</p>
      <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">{journey.name}</h1>
      <p className="text-sm text-white/60 mt-2">{journey.description}</p>
      <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
        <div className="h-full bg-[#D4AF37] rounded-full transition-all" style={{ width: `${pct}%` }} />
      </div>
      <p data-testid="journey-progress" className="text-[11px] text-white/50 mt-2">
        {done.length} / {journey.steps.length} stops · +{journey.reward_points} pts on completion
      </p>

      <div className="mt-8 space-y-4">
        {journey.steps.map((step, i) => {
          const isDone = done.includes(i);
          const dish = step.dish_details;
          return (
            <div key={i} data-testid={`journey-step-${i}`} className={`rounded-2xl border overflow-hidden ${isDone ? "border-[#D4AF37]/50" : "border-white/10"} bg-[#121212]`}>
              <div className="flex gap-3 p-3">
                <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${isDone ? "bg-[#D4AF37] text-black" : "bg-white/10 text-white/60"}`}>
                  {isDone ? <Check size={14} /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold">{step.title}</p>
                  <p className="text-[11px] text-white/50">{step.note}</p>
                  {dish && (
                    <Link to={`/experience/dish/${dish.slug}`} data-testid={`journey-step-dish-${i}`} className="flex items-center gap-2 mt-2 rounded-xl bg-[#1A1A1A] border border-white/10 p-2">
                      <img src={dish.image} alt={dish.name} className="w-10 h-10 rounded-lg object-cover" />
                      <span className="text-xs flex-1">{dish.name}</span>
                      <span className="text-xs text-[#D4AF37] font-bold">{money(dish.price)}</span>
                    </Link>
                  )}
                </div>
              </div>
              {!isDone && (
                <button data-testid={`journey-step-complete-${i}`} onClick={() => completeStep(i)}
                  className="w-full py-2.5 text-xs font-bold tracking-widest text-[#D4AF37] border-t border-white/10 hover:bg-[#D4AF37]/5">
                  MARK THIS STOP EXPLORED
                </button>
              )}
            </div>
          );
        })}
      </div>

      {journey.completed && (
        <div data-testid="journey-complete-banner" className="mt-6 rounded-2xl border border-[#D4AF37]/50 bg-[#D4AF37]/10 p-5 text-center">
          <p className="font-display text-2xl font-bold text-[#D4AF37]">JOURNEY COMPLETE</p>
          <p className="text-xs text-white/60 mt-1">Achievement unlocked — check your Passport.</p>
        </div>
      )}
    </div>
  );
}
